import {
    Form,
    FormField,
    FormSection,
    FormType,
    InputType,
    FilteredValue,
    type DropdownData,
    ReactiveFormChangedField,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { ParcelasEnvironmentDefinition } from '../environment-variables';
import { ParcelasListsDefinition } from './list-variables';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { ParcelasModel } from '../../../../../models/form-common/parcelas.model';
import { SectorModel } from '../../../../../models/form-common/sector.model';
import { VisitasVariedadMesModel } from '../../../../../models/visitas_variedad_mes/visitas_variedad_mes.model';
import { list } from '../../../../../common/classes/request-builder';
import { DialogSigPacComponent } from './dialog/dialog.sigpac.component';
import { environment } from '../../../../../../environments/environment';
import {
    getDropdownSelected,
    getDropdownSelectedArray,
    getDropdownSelectedSingle,
    setDropdownSelected,
    setDropdownSelectedFromArray,
} from '@fitogest/reactive-form-builder/utils';

export class ParcelasFormDefinition {
    public sigpacDialog: DialogSigPacComponent | undefined;

    public formType!: FormType;
    public env: ParcelasEnvironmentDefinition = new ParcelasEnvironmentDefinition();
    public lists: ParcelasListsDefinition = new ParcelasListsDefinition();

    public cliente = StorageManager.getClient();
    public usuario = StorageManager.getUser();

    public model: ReactiveFormValue<ParcelasModel> = {} as ReactiveFormValue<ParcelasModel>;

    public tag: ReactiveFormChangedField<ParcelasModel> = '';

    public formFieldsDatosParcela!: FormSection<object>;
    public formFieldsNumMetros!: FormSection<object>;
    public formFieldsDatosParcelaOtros!: FormSection<object>;
    public formFieldsDatosSigpac!: FormSection<object>;
    public formFieldsDatosGenerales!: FormSection<object>;
    public formFieldsDatosMedioambientales!: FormSection<object>;
    public formFieldsZonasEspecificas!: FormSection<object>;

    public formFields!: Form<ParcelasModel>;
    public fieldNames: FormField<any>[] = [];
    public fieldLabels: string[] = [];

    public now = new Date();
    public day = this.now.getDate().toString().padStart(2, '0');
    public month = (this.now.getMonth() + 1).toString().padStart(2, '0');
    public year = this.now.getFullYear().toString();
    public hours = this.now.getHours().toString().padStart(2, '0');
    public minutes = this.now.getMinutes().toString().padStart(2, '0');

    public viewNumeroMetros = false;
    public mapaOpened = false;
    public mapaAbierto = false;

    public finca = 'finca';
    public localidad = 'localidad';
    public parcelaNombre = 'nombre parcela';
    public cultivo = 'cultivo';
    public variedad = 'variedad';

    public openDialog = false;

    private readDropdownOption<TItem extends Record<string, unknown>>(option: FilteredValue<unknown> | null | undefined): TItem | null {
        const rawValue = option?.raw;

        if (rawValue !== null && typeof rawValue === 'object') {
            return rawValue as TItem;
        }

        const value = option?.value;

        return value !== null && typeof value === 'object' ? value as TItem : null;
    }

    constructor(
        type: FormType,
        _model: Record<string, string>,
        sigpacDialog: DialogSigPacComponent | undefined,
        table: (string[] | string[][])[] | null) {
        this.formType = +type;
        this.sigpacDialog = sigpacDialog;
        this.model.cultivo = this.resolveCurrentCultivoLabel();
        this.model.variedad = this.resolveCurrentVariedadLabel();


        this.formFields = {
            name: 'parcelas',
            header: 'Datos de ' + environment.features.parcelaName,
            type: this.formType,
            sections: this.getFormSections(),
            table: { title: '', showIndex: false, data: table?.[0], style: table?.[1] },
            uploadButton: {
                isEnabled: false,
                label: ''
            },
            signed: false
        };
    }


    public getDatosParcelaFields() {
        const datosParcelaFields: FormField<any>[] = [
            {
                name: this.formType < 3 ? 'id_finca' : 'finca',
                vinculatedFields: undefined,
                label: this.env.fincaName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.fincas,
                    isEditable: true,
                    dropdownConfiguration: { valuePrimaryKey: 'id' }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            },
            {
                name: this.formType < 3 ? 'id_sector' : 'sector',
                dependencies: [
                    {
                        source: 'id_finca',
                        target: this.formType < 3 ? 'id_sector' : 'sector',
                        kind: 'filter'
                    }
                ],
                vinculatedFields: undefined,
                label: this.env.sectorName,
                input: {
                    type: InputType.DROPDOWN,
                    data: this.lists.sectores,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it: FilteredValue<SectorModel>) => it.value?.id_finca === getDropdownSelectedSingle(this.lists.fincas)?.id,
                    },
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showSectores
                },
                order: 2
            },
            {
                name: 'numero_parcela_agricola',
                vinculatedFields: undefined,
                label: 'Número de Parcela Agrícola',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'agropecuariaramirogruas' || this.env.appName === 'prodalbar'
                },
                order: 3
            },
            {
                name: 'pendiente',
                vinculatedFields: undefined,
                label: '% Pendiente',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'agropecuariaramirogruas'
                },
                order: 4
            },
            {
                name: this.formType < 3 ? 'id_cultivo' : 'cultivo',
                vinculatedFields: undefined,
                label: this.env.hasPlantas ?
                    'Género/Especie SIEX' :
                    this.env.showTareasMto ?
                        'Especie SIEX' :
                        'Cultivo SIEX',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.cultivos,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        distinctBy: ['raw.codigo', 'label'],
                        filter: (it: FilteredValue<VisitasVariedadMesModel>) => {
                            const cultivo = this.readDropdownOption<VisitasVariedadMesModel>(it)?.cultivo;

                            if (this.env.appName !== 'yagoaznar') {
                                return true;
                            }

                            return this.lists.filtroCultivosYagoAznar.some((allowedCultivo) => allowedCultivo === cultivo);
                        }
                    },
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: !this.env.showTareasMto && this.env.appName !== 'agrosalvi' && this.env.appName !== 'detorres',
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.activateApiSIEX
                },
                order: 5,
            },
            {
                name: this.formType < 3 ? 'id_variedad' : 'variedad',
                vinculatedFields: ['id_cultivo'],
                label: 'Variedad SIEX',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.variedades,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        autoSelect: 'singleOption',
                        distinctBy: ['raw.codigo_cultivo', 'raw.codigo_variedad', 'label']
                    }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: (!this.env.variedadCanBeVarios && !this.env.showTareasMto) && this.env.appName !== 'agrosalvi',
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.activateApiSIEX,
                },
                order: 6,
            },
            {
                name: this.formType < 3 ? 'id_cultivo_legacy' : 'cultivo_legacy',
                vinculatedFields: undefined,
                label: this.env.appName === 'momparler' ? 'Género/Especie' : this.env.showTareasMto ? 'Especie' : 'Cultivo',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.cultivosLegacy,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id'
                    }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: !this.env.showTareasMto && this.env.appName !== 'agrosalvi',
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showCultivosVariedadesPropios ? (this.env.hasPlantas || !this.env.activateApiSIEX) : false
                },
                order: 7
            },
            {
                name: this.formType < 3 ? 'id_variedad_legacy' : 'variedad_legacy',
                dependencies: [
                    {
                        source: 'id_cultivo_legacy',
                        target: this.formType < 3 ? 'id_variedad_legacy' : 'variedad_legacy',
                        kind: 'filter'
                    }
                ],
                vinculatedFields: undefined,
                label: 'Variedad',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.variedadesLegacy,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it: FilteredValue<VisitasVariedadMesModel>) => {
                            const selectedCultivo = getDropdownSelectedSingle(this.lists.cultivosLegacy);
                            const match = it.value?.cultivo === selectedCultivo?.cultivo;

                            if (match) {
                                setDropdownSelected(
                                    this.lists.cultivosLegacy,
                                    this.lists.cultivosLegacy.filtered
                                        .filter(c => c && c.value)
                                        .map(c => c.value)
                                        .find(cultivo => cultivo?.cultivo === it.value?.cultivo) as VisitasVariedadMesModel | null
                                );
                            }

                            return match;
                        },
                    },
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: (!this.env.variedadCanBeVarios && !this.env.showTareasMto) && this.env.appName !== 'agrosalvi',
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showCultivosVariedadesPropios ? (this.env.hasPlantas || !this.env.activateApiSIEX) : false
                },
                order: 8
            },
            {
                name: 'marco',
                vinculatedFields: undefined,
                label: this.env.appName === 'cartagenafresh' ? 'Nº Cintas Riego' : 'Marco Plantación (Filas x Plantas)',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showMarcoPlantacion
                },
                order: 9
            },
            {
                name: 'pies',
                vinculatedFields: undefined,
                label: 'Pies',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'yagoaznar'
                },
                order: 10
            },
            {
                name: 'anyo_plantacion',
                vinculatedFields: undefined,
                label: this.env.appName === 'cartagenafresh' ? 'Semana Plantación' : 'Año Plantación',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showAnyoPlantacion
                },
                order: 11
            },
            {
                name: 'plantas',
                vinculatedFields: undefined,
                label: this.env.appName === 'cartagenafresh' ? 'Kg/Ha' : 'Nº Árboles/Plantas',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showPlantas
                },
                order: 12
            },
            {
                name: 'patron',
                vinculatedFields: undefined,
                label: getDropdownSelectedSingle(this.lists.cultivos)?.cultivo === 'VIÑA' ?
                    'Portainjertos' :
                    this.env.appName === 'momparler' ?
                        'Formato' :
                        this.env.appName === 'cartagenafresh' ?
                            'Cama' :
                            'Patrón',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.env.appName === 'momparler' ? this.lists.formatoMomparler :
                        this.env.appName === 'cartagenafresh' ? this.lists.cama : this.lists.patron,
                    isEditable: true,
                    dropdownConfiguration: {
                        swap: (it: FormField<object>) => {
                            it.input.data = this.getPatron(this.model.cultivo ?? '');
                            if (this.model.cultivo === 'viña') {
                                it.label = 'Portainjertos';
                            } else {
                                if (this.env.appName === 'momparler') {
                                    it.label = 'Formato';
                                    //this.parcelaNombreCultivo = cultivo;
                                } else {
                                    it.label = 'Patrón';
                                }
                            }
                        },
                    }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showPatron
                },
                order: 13
            },
            {
                name: 'diametro_copa',
                vinculatedFields: undefined,
                label: 'Diámetro de Copa',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'agufert'
                },
                order: 14
            },
            {
                name: 'captacion_agua',
                vinculatedFields: undefined,
                label: 'Captación Agua',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'agropecuariaramirogruas'
                },
                order: 15
            },
            {
                name: 'zonas_especificas',
                vinculatedFields: undefined,
                label: 'Zonas Específicas',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'agropecuariaramirogruas'
                },
                order: 16
            },
            {
                name: 'es_zona_vulnerable',
                vinculatedFields: undefined,
                label: 'Zona Vulnerable',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'prosanzcu'
                },
                order: 17
            },
            {
                name: 'zona_vulnerable',
                vinculatedFields: undefined,
                label: 'Zona Vulnerable (ha)',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'agropecuariaramirogruas'
                },
                order: 18
            },
            {
                name: 'herbaceo_lenyoso',
                vinculatedFields: undefined,
                label: 'Herbáceo / Leñoso',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'agropecuariaramirogruas'
                },
                order: 19
            },
            {
                name: 'producto',
                vinculatedFields: undefined,
                label: 'Producto',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'bravosol'
                },
                order: 20
            },
            {
                name: 'prevision_produccion',
                vinculatedFields: undefined,
                label: 'Previsión Producción',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'icod'
                },
                order: 21
            },
            {
                name: 'codigo_trazabilidad',
                vinculatedFields: undefined,
                label: 'Código de trazabilidad',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showParcelasCodigoTrazabilidad
                },
                order: 22
            },
            {
                name: 'lote',
                vinculatedFields: undefined,
                label: 'Nº lote',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showParcelasLote
                },
                order: 23
            },
            {
                name: 'pozo',
                vinculatedFields: undefined,
                label: 'Pozo',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'pyf'
                },
                order: 24
            },
            {
                name: 'rios',
                vinculatedFields: undefined,
                label: 'Rios',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'pyf'
                },
                order: 25
            },
            {
                name: 'estacion_control',
                vinculatedFields: undefined,
                label: 'Estación de control',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'pyf'
                },
                order: 26
            },
            {
                name: 'regimen_tenencia',
                vinculatedFields: undefined,
                label: 'Régimen tenencia',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.env.appName === 'prodalbar' ? this.lists.regimenTenenciaProdalbar : this.lists.regimenTenencia,
                    isEditable: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'pyf' || this.env.appName === 'prodalbar'
                },
                order: 27
            },
            {
                name: 'produccion_ecologica',
                vinculatedFields: undefined,
                label: 'Producción Ecológica',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'prodalbar'
                },
                order: 28
            },
            {
                name: 'uds_bandeja',
                vinculatedFields: undefined,
                label: 'Uds. Bandeja',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.lists.uds_bandeja,
                    isEditable: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'viverospereira'
                },
                order: 29
            },
            {
                name: 'formato',
                vinculatedFields: undefined,
                label: 'Formato',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.formatosViverogest,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'codigo'
                    }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.applicationType === 'viverogest'
                },
                order: 30
            },
            {
                name: 'distancia_menor_50',
                vinculatedFields: undefined,
                label: 'DISTANCIA MENOR A 50m',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'agropecuariaramirogruas'
                },
                updateValue: (it: any) => it.input.data === 1 ? this.viewNumeroMetros = true : false,
                order: 31
            },
            {
                name: 'tipo_siembra',
                vinculatedFields: undefined,
                label: 'Tipo de Siembra',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.lists.tipoSiembra,
                    isEditable: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'pedrosaez'
                },
                order: 32
            },
            {
                name: 'aplicadores',
                vinculatedFields: undefined,
                label: this.env.appName === 'avafitogest' ? 'Asesor' : 'Aplicadores',
                input: {
                    type: this.env.appName === 'avafitogest' ? InputType.DROPDOWN : InputType.MULTISELECT,
                    data: this.lists.aplicadores,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it: FilteredValue<any>) => {
                            if (
                                this.env.appName === 'sequiador' &&
                                it.value?.rol === 'responsable' &&
                                it.value.acceso_plataforma?.toString() === '1'
                            ) {
                                // Asesor la Comunidad de Regantes de Alfafar. Debe tener acceso a todos los cuadernos de campo.
                                if (it.value.nombre === 'TONI') {
                                    const selectedAplicadores = getDropdownSelectedArray(this.lists.aplicadores);
                                    if (selectedAplicadores.indexOf(it.value) < 0) {
                                        setDropdownSelectedFromArray(
                                            this.lists.aplicadores,
                                            [...selectedAplicadores, it.value],
                                            true
                                        );
                                    }
                                }
                                return true;
                            }

                            if (this.env.appName === 'avafitogest' && (Boolean((it.value?.tipo_carnet.includes('ASESOR'))))) {
                                return true;
                            }

                            return false;
                        }
                    },
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: (this.env.appName === 'sequiador' &&
                        (StorageManager.getUser().tipo === 'comunero' ||
                            StorageManager.getUser().rol === 'admin')) || this.env.appName === 'avafitogest',
                },
                order: 33
            },
            {
                name: this.formType < 3 ? 'id_responsable_asignado' : 'responsable_nombre',
                vinculatedFields: undefined,
                label: 'Grabador',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.usuarios,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it: FilteredValue<any>) => {
                            if (
                                it.value?.rol === 'responsable' &&
                                it.value.acceso_plataforma?.toString() === '1'
                            ) {
                                return true;
                            } else {
                                return false;
                            }
                        }
                    },
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'avafitogest',
                },
                order: 34
            },
            {
                name: this.formType < 3 ? 'id_comunidad_regantes' : 'comunidad_regantes',
                vinculatedFields: undefined,
                label: 'Comunidad de Regantes',
                input: {
                type: InputType.DROPDOWN_SEARCH,
                isEditable: true,
                data: this.lists.comunidadesRegantes,
                dropdownConfiguration: {
                    valuePrimaryKey: 'id'
                }
                },
                width: 3,
                conditions: {
                isRememberedOnDuplicate: true,
                isRequired: false,
                isRetained: false,
                hasUndoEnabled: false,
                isVisible: true,
                },
                order: 35,
                buttons: [
                    {
                    class: 'btn btn-success border rounded-circle shadow-sm p-0 d-flex align-items-center justify-content-center',
                    style: `
                        width: 26px;
                        height: 26px;
                        margin-left: 6px;
                        font-size: .95rem;
                        transform: translateY(-0.5rem);
                    `,
                    icon: 'fa fa-plus',
                    label: '',
                    tooltip: 'Añadir nuevo valor',
                    tooltipPosition: 'top',
                    function: () => this.openDialog = true
                    }
                ],
            },
            {
                name: 'activo',
                vinculatedFields: undefined,
                label: 'Activo',
                input: { type: InputType.SWITCH, defaultValue: '1', isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showAplicadoresActivo
                },
                order: 36
            },
            {
                name: this.formType < 3 ? 'codigo_procedencia_agua' : 'procedencia_agua',
                vinculatedFields: undefined,
                label: 'Procedencia Agua/Origen',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.lists.origenesAguaRiego,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'codigo_SIEX'
                    }
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.applicationType === 'cropgest',
                },
                order: 37
            },
            {
                name: 'linde',
                vinculatedFields: undefined,
                label: 'Linde',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.applicationType === 'cropgest'
                },
                order: 38
            },
            {
                name: 'gip',
                vinculatedFields: undefined,
                label: 'GIP',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.applicationType === 'cropgest'
                },
                order: 39
            },
            {
                name: 'masas_agua',
                vinculatedFields: undefined,
                label: 'Masas de agua superficie',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.applicationType === 'cropgest'
                },
                order: 40
            },
            {
                name: 'puntos_agua',
                vinculatedFields: undefined,
                label: 'Puntos de extracción de agua para consumo humano ',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.applicationType === 'cropgest'
                },
                order: 41
            },
            {
                name: 'codigo_DGC',
                vinculatedFields: undefined,
                label: 'Codigo DGC',
                input: { type: InputType.TEXT, isEditable: false },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: environment.features.activateREA
                },
                order: 42
            },
            {
                name: 'notas',
                vinculatedFields: undefined,
                label: 'Notas',
                input: { type: InputType.TEXTAREA, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'agroleza'
                },
                order: 43
            }
        ];
        this.fieldNames = this.fieldNames.concat(datosParcelaFields.filter((it) => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(datosParcelaFields.filter((it) => it.conditions.isVisible).map(it => it.label));
        return datosParcelaFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    public getNumMetrosFields(isSectionVisible: boolean) {
        const numMetrosFields: FormField<any>[] = [
            {
                name: 'numero_metros',
                vinculatedFields: undefined,
                label: 'Número de Metros',
                input: { type: InputType.TEXT, isEditable: true },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: isSectionVisible
                },
                order: 1
            },
        ];
        this.fieldNames = this.fieldNames.concat(numMetrosFields.filter((it) => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(numMetrosFields.filter((it) => it.conditions.isVisible).map(it => it.label));
        return numMetrosFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    public getDatosParcelaOtrosFields(isSectionVisible: boolean) {
        const datosParcelaOtrosFields: FormField<any>[] = [
            {
                name: 'partida',
                vinculatedFields: undefined,
                label: 'Partida',
                input: { type: InputType.TEXT, isEditable: true },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: isSectionVisible && this.env.showParcelasPartida
                },
                order: 1
            },
            {
                name: 'comercio',
                vinculatedFields: undefined,
                label: 'Comercio',
                input: { type: InputType.TEXT, isEditable: true },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: isSectionVisible && this.env.showParcelasComercio && !this.env.showParcelasComercioDropdown
                },
                order: 2
            },
            {
                name: 'comercio',
                vinculatedFields: undefined,
                label: 'Comercio',
                input: {
                    type: InputType.TEXT,
                    data: this.lists.comercios,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id'
                    }
                },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: isSectionVisible && this.env.showParcelasComercioDropdown
                },
                order: 3
            },
            {
                name: 'pozos',
                vinculatedFields: undefined,
                label: 'Pozos',
                input: { type: InputType.TEXT, isEditable: true },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: isSectionVisible && this.env.showParcelasPozos
                },
                order: 4
            },
            {
                name: 'turbo',
                vinculatedFields: undefined,
                label: this.model.turbo ? 'Turbo Sí' : 'Turbo No',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: isSectionVisible && this.env.showParcelasTurbo
                },
                order: 5
            },
        ];
        this.fieldNames = this.fieldNames.concat(datosParcelaOtrosFields.filter((it) => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(datosParcelaOtrosFields.filter((it) => it.conditions.isVisible).map(it => it.label));
        return datosParcelaOtrosFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    public getDatosSigpacFields() {
        const datosSigpacFields: FormField<any>[] = [
            {
                name: 'nombre',
                vinculatedFields: undefined,
                label: `Nombre ${this.env.appName === 'gardengest' ? 'del' : 'de la'} ${this.env.parcelaName}`,
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            },
            {
                name: 'uso_sigpac',
                vinculatedFields: undefined,
                label: 'Uso SigPAC',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.lists.usoSigpac,
                    isEditable: true
                },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 2
            },
            {
                name: 'superficie_sigpac',
                vinculatedFields: undefined,
                label: 'Superficie SigPAC (' + this.env.textoSuperficie + ')',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 3
            },
            {
                name: 'superficie_cultivada',
                vinculatedFields: undefined,
                label: 'Superficie Cultivada (' + this.env.textoSuperficie + ')',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 4
            },
            {
                name: 'cod_provincia',
                vinculatedFields: undefined,
                label: 'Cod. Provincia',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 5
            },
            {
                name: 'municipio',
                vinculatedFields: undefined,
                label: 'Municipio',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 6
            },
            {
                name: 'poligono',
                vinculatedFields: undefined,
                label: 'Polígono',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 7
            },
            {
                name: 'numero',
                vinculatedFields: undefined,
                label: 'Nº Parcela',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 8
            },
            {
                name: 'recinto',
                vinculatedFields: undefined,
                label: 'Recinto',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 9
            },
            {
                name: 'agregado',
                vinculatedFields: undefined,
                label: 'Agregado',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 10
            },
            {
                name: 'zona',
                vinculatedFields: undefined,
                label: 'Zona',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 11
            },
            {
                name: 'sigpac',
                vinculatedFields: undefined,
                label: 'SigPAC',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 12
            },
            {
                name: 'coordenadas',
                vinculatedFields: undefined,
                label: 'Coordenadas',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'agrosalvi'
                },
                order: 13
            }
        ];
        this.fieldNames = this.fieldNames.concat(datosSigpacFields.filter((it) => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(datosSigpacFields.filter((it) => it.conditions.isVisible).map(it => it.label));
        return datosSigpacFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    public getDatosGeneralesFields() {
        const datosGeneralesFields: FormField<any>[] = [
            {
                name: 'codigo_secano_regadio',
                vinculatedFields: undefined,
                label: 'Secano / Regadío',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.lists.secanoRegadio,
                    isEditable: true
                },
                width: 4,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            },
            {
                name: 'codigo_sistema_riego',
                dependencies: [
                    {
                        source: 'codigo_secano_regadio',
                        target: 'codigo_sistema_riego',
                        kind: 'filter'
                    }
                ],
                vinculatedFields: undefined,
                label: 'Sistema de Riego',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.lists.sistemasRiego,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'codigo_SIEX',
                        filter: () => getDropdownSelectedSingle(this.lists.secanoRegadio) === 'R',
                    },
                },
                width: 4,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 2
            },
            {
                name: 'zona_riego',
                vinculatedFields: undefined,
                label: 'Zona de Riego',
                input: {
                    type: InputType.DROPDOWN,
                    data: list('...', 'Zona Alta', 'Zona Baja'),
                    isEditable: true,
                },
                width: 4,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.appName === 'avafitogest'
                },
                order: 3
            },
            {
                name: 'codigo_sistema_cultivo',
                vinculatedFields: undefined,
                label: 'Sistema de Cultivo',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.lists.sistemasCultivo,
                    isEditable: true
                },
                width: 4,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 4
            },
            {
                name: 'codigo_observaciones_forestales',
                vinculatedFields: ['codigo_sistema_cultivo'],
                label: 'Observaciones de Forestales',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.lists.observacionesForestales,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'codigo',
                    }
                },
                width: 4,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 5
            },
            {
                name: 'sistema_asesoramiento',
                vinculatedFields: undefined,
                label: 'Sistema de asesoramiento',
                input: {
                    type: InputType.DROPDOWN,
                    data: this.lists.sistemaAsesoramiento,
                    isEditable: true
                },
                width: 4,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 6
            },
            {
                name: 'estacion',
                vinculatedFields: undefined,
                label: 'Estación',
                input: {
                    type: InputType.DROPDOWN,
                    data: list(['...', null], ['I - Invierno', 'I'], ['P - Primavera', 'P'], ['V - Verano', 'V']),
                    isEditable: true
                },
                width: 4,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.showParcelasEstacion && this.env.appName !== 'pyf'
                },
                order: 6
            },

        ];
        this.fieldNames = this.fieldNames.concat(datosGeneralesFields.filter((it) => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(datosGeneralesFields.filter((it) => it.conditions.isVisible).map(it => it.label));
        return datosGeneralesFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    public getDatosMedioambientalesFields(isSectionVisible: boolean) {
        const datosMedioambientalesFields: FormField<any>[] = [
            {
                name: 'incluido_en_parcela',
                vinculatedFields: undefined,
                label: 'Incluido en la parcela',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: isSectionVisible
                },
                order: 1
            },
            {
                name: 'distancia',
                vinculatedFields: undefined,
                label: 'Distancia (m3)',
                input: { type: InputType.TEXT, isEditable: true },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: isSectionVisible
                },
                order: 2
            },
            {
                name: 'coordenadas_utm',
                vinculatedFields: undefined,
                label: 'Coordenadas UTM',
                input: { type: InputType.TEXT, isEditable: true },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: isSectionVisible
                },
                order: 3
            },
            {
                name: 'denominacion',
                vinculatedFields: undefined,
                label: 'Denominación',
                input: { type: InputType.TEXT, isEditable: true },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: isSectionVisible
                },
                order: 4
            }
        ];
        this.fieldNames = this.fieldNames.concat(datosMedioambientalesFields.filter((it) => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(datosMedioambientalesFields.filter((it) => it.conditions.isVisible).map(it => it.label));
        return datosMedioambientalesFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    public getZonasEspecificasFields(isSectionVisible: boolean) {
        const zonasEspecificasFields: FormField<any>[] = [
            {
                name: 'zona_total',
                vinculatedFields: undefined,
                label: 'Totalmente',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: isSectionVisible
                },
                order: 1
            },
            {
                name: 'zona_parcial',
                vinculatedFields: undefined,
                label: 'Parcialmente',
                input: { type: InputType.SWITCH, isEditable: true },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: isSectionVisible
                },
                order: 2
            }
        ];
        this.fieldNames = this.fieldNames.concat(zonasEspecificasFields.filter((it) => it.conditions.isVisible));
        this.fieldLabels = this.fieldLabels.concat(zonasEspecificasFields.filter((it) => it.conditions.isVisible).map(it => it.label));
        return zonasEspecificasFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }


    private getFormSections() {
        const sections: FormSection<any>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getDatosParcelaFields(),
                order: 1
            },
            {
                name: '',
                width: 12,
                isVisible: this.viewNumeroMetros,
                fields: this.getNumMetrosFields(this.viewNumeroMetros),
                order: 2
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getDatosParcelaOtrosFields(this.env.otros),
                order: 3
            },
            {
                name: 'Datos SigPAC',
                width: 12,
                isVisible: true,
                fields: this.getDatosSigpacFields(),
                buttons: [{
                    class: 'btn btn-warning text-nowrap',
                    style: 'margin-left:0px !important',
                    icon: 'fa fa-search',
                    label: ' Mapa',
                    isVisible: environment.appName !== 'agrogestSIEX',
                    function: () => this.abreMapa()
                },
                {
                    class: 'btn text-nowrap',
                    style: 'margin-left:0px !important; background-color: #16A2B7; color: white',
                    icon: 'fa fa-map-marker',
                    label: ' SigPAC',
                    isVisible: environment.appName !== 'agrogestSIEX',
                    function: () => this.openSigpac()
                }
                ],
                order: 4,
            },
            {
                name: 'Datos generales',
                width: 12,
                isVisible: true,
                fields: this.getDatosGeneralesFields(),
                order: 5
            },
            {
                name: 'Puntos de captación de agua',
                width: 6,
                isVisible: this.env.showDatosMedioambientales,
                fields: this.getDatosMedioambientalesFields(this.env.showDatosMedioambientales),
                order: 6
            },
            {
                name: 'Parcelas en zonas específicas',
                width: 6,
                isVisible: this.env.showDatosMedioambientales,
                fields: this.getZonasEspecificasFields(this.env.showDatosMedioambientales),
                order: 7
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 8
            }
        ];
        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getHiddenFields() {
        const hiddenFields: FormField<any>[] = [
            {
                name: 'id',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id,
                    isEditable: false
                },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true,
                },
                order: 1
            },
            {
                name: 'id_cliente',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.cliente?.id ? this.cliente?.id : getDropdownSelectedSingle(this.lists.fincas)?.id_cliente,
                    isEditable: false
                },
                updateValue: () => this.cliente?.id ? this.cliente?.id : getDropdownSelectedSingle(this.lists.fincas)?.id_cliente,
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.hasClients
                },
                order: 2
            },
            {
                name: 'id_usuario',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: !this.env.showTareasMto ?
                        this.usuario ? this.usuario.id :
                            getDropdownSelectedSingle(this.lists.fincas)?.id_usuario :
                        !FormType.EDITION ? this.usuario.id : '',
                    isEditable: false
                },
                updateValue: () => !this.env.showTareasMto ?
                    this.usuario ? this.usuario.id :
                        getDropdownSelectedSingle(this.lists.fincas)?.id_usuario :
                    !FormType.EDITION ? this.usuario.id : '',
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 3
            },
            {
                name: 'cultivo',
                vinculatedFields: ['id_cultivo', 'id_cultivo_legacy'],
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                updateValue: () => this.resolveCurrentCultivoLabel(),
                order: 4
            },
            {
                name: 'variedad',
                vinculatedFields: ['id_variedad', 'id_variedad_legacy'],
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                updateValue: () => this.resolveCurrentVariedadLabel(),
                order: 5
            },
            {
                name: 'uds_bandeja',
                vinculatedFields: ['formato'],
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    isEditable: false
                },
                updateValue: () => {
                    const selected = getDropdownSelectedSingle(this.lists.formatosViverogest);
                    return selected?.uds_bandeja?.toString() ?? this.model.uds_bandeja ?? '';
                },
                width: 2,
                conditions: {
                    isRememberedOnDuplicate: true,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: this.env.applicationType === 'viverogest'
                },
                order: 6
            },
        ];

        if (this.formType === FormType.DUPLICATION) {
            hiddenFields.splice(1, 1);
        }

        return hiddenFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getPatron(cultivo: string) {
        switch (cultivo) {
            case 'aguacate': return this.lists.patronAguacate;
            case 'almendro': return this.lists.patronAlmendro;
            case 'cerezo': return this.lists.patronCerezo;
            case 'ciruelo': return this.lists.patronCiruelo;
            case 'cítrico': return this.lists.patronCitricos;
            case 'cítrico mandarino': return this.lists.patronCitricos;
            case 'clementina': return this.lists.patronCitricos;
            case 'lima': return this.lists.patronCitricos;
            case 'limoneros': return this.lists.patronCitricos;
            case 'mandarinos': return this.lists.patronCitricos;
            case 'mandarino': return this.lists.patronCitricos;
            case 'mandarino híbrido': return this.lists.patronCitricos;
            case 'mango': return this.lists.patronMango;
            case 'manzano': return this.lists.patronManzano;
            case 'melocotonero': return this.env.appName === 'agropedbel' ?
                this.lists.patronMelocotoneroParaguayo :
                this.lists.patronMelocotonero;
            case 'naranjos': return this.lists.patronCitricos;
            case 'naranjo': return this.lists.patronCitricos;
            case 'nogal': return this.lists.patronNogal;
            case 'pera': return this.lists.patronPera;
            case 'peral': return this.lists.patronPera;
            case 'pomelo': return this.lists.patronCitricos;
            case 'pomelos': return this.lists.patronCitricos;
            case 'viña': return this.lists.patronVina;
            case 'paraguayo': return this.lists.patronMelocotoneroParaguayo;
            default: if (this.env.appName === 'momparler') { return this.lists.formatoMomparler; } else { return this.lists.patron; };
        }
    }

    private abreMapa() {
        this.mapaOpened = true;

        const fincaSelected = this.resolveSelectedRaw(this.lists.fincas, this.model.id_finca);

        if (!this.toStringValue(fincaSelected?.['id'] ?? getDropdownSelectedSingle(this.lists.fincas)?.id ?? this.model.id_finca)) {
            alert('Debe seleccionar una Finca!');
            return;
        }

        const cultivoSelected = this.resolveCurrentCultivoForMap();
        const variedadSelected = this.resolveCurrentVariedadForMap();

        if (cultivoSelected.id === '') {
            alert('Debe seleccionar un Cultivo!');
            return;
        }
        if (variedadSelected.id === '' && !this.env.variedadCanBeVarios) {
            alert('Debe seleccionar una Variedad!');
            return;
        }

        this.mapaAbierto = !this.mapaAbierto;
        this.finca = this.toStringValue(fincaSelected?.['nombre'] ?? getDropdownSelectedSingle(this.lists.fincas)?.nombre);
        this.localidad = this.toStringValue(fincaSelected?.['localidad'] ?? getDropdownSelectedSingle(this.lists.fincas)?.localidad);
        this.cultivo = cultivoSelected.label;
        this.variedad = this.env.variedadCanBeVarios && variedadSelected.id === '' ? 'Varios' : variedadSelected.label;
        this.parcelaNombre = this.toStringValue(this.model.nombre);
    }

    private resolveCurrentCultivoForMap(): { id: string; label: string } {
        const dropdown = this.env.activateApiSIEX ? this.lists.cultivos : this.lists.cultivosLegacy;
        const currentValue = this.env.activateApiSIEX ? this.model.id_cultivo : this.model.id_cultivo_legacy ?? this.model.id_cultivo;
        const rawValue = this.resolveSelectedRaw(dropdown, currentValue);
        const id = this.resolveFirstValue(
            rawValue?.['id'],
            rawValue?.['codigo'],
            rawValue?.['codigo_cultivo'],
            rawValue?.['id_cultivo'],
            this.extractCatalogKeyPart(currentValue, 1),
            currentValue,
        );
        const label = this.resolveFirstValue(rawValue?.['cultivo'], this.model.cultivo, this.model.cultivo_legacy, id);

        return { id, label };
    }

    private resolveCurrentVariedadForMap(): { id: string; label: string } {
        const dropdown = this.env.activateApiSIEX ? this.lists.variedades : this.lists.variedadesLegacy;
        const currentValue = this.env.activateApiSIEX ? this.model.id_variedad : this.model.id_variedad_legacy ?? this.model.id_variedad;
        const rawValue = this.resolveSelectedRaw(dropdown, currentValue);
        const id = this.resolveFirstValue(
            rawValue?.['id'],
            rawValue?.['id_variedad'],
            rawValue?.['codigo_variedad'],
            this.extractCatalogKeyPart(currentValue, 2),
            currentValue,
        );
        const label = this.resolveFirstValue(rawValue?.['variedad'], this.model.variedad, id);

        return { id, label };
    }

    private resolveCurrentCultivoLabel(): string {
        const dropdown = this.shouldUseLegacyCultivoCatalog() ? this.lists.cultivosLegacy : this.lists.cultivos;
        const currentValue = this.shouldUseLegacyCultivoCatalog()
            ? this.model.id_cultivo_legacy ?? this.model.id_cultivo
            : this.model.id_cultivo;
        const rawValue = this.resolveSelectedRaw(dropdown, currentValue);

        return this.resolveFirstValue(rawValue?.['cultivo'], this.model.cultivo, this.model.cultivo_legacy);
    }

    private resolveCurrentVariedadLabel(): string {
        const dropdown = this.shouldUseLegacyCultivoCatalog() ? this.lists.variedadesLegacy : this.lists.variedades;
        const currentValue = this.shouldUseLegacyCultivoCatalog()
            ? this.model.id_variedad_legacy ?? this.model.id_variedad
            : this.model.id_variedad;
        const rawValue = this.resolveSelectedRaw(dropdown, currentValue);

        return this.resolveFirstValue(rawValue?.['variedad'], this.model.variedad);
    }

    private shouldUseLegacyCultivoCatalog(): boolean {
        return this.env.showCultivosVariedadesPropios && (this.env.hasPlantas || !this.env.activateApiSIEX);
    }

    private resolveSelectedRaw<TItem extends Record<string, unknown>>(
        dropdown: DropdownData<unknown>,
        currentValue: unknown,
    ): TItem | null {
        const currentAliases = this.toComparableAliases(currentValue);
        const selectedRaw = this.asRecord<TItem>(getDropdownSelected(dropdown));

        if (selectedRaw && (currentAliases.length === 0 || this.rawMatchesAliases(selectedRaw, currentAliases))) {
            return selectedRaw;
        }

        const option = [...dropdown.filtered, ...dropdown.values]
            .find((item) => this.optionMatchesAliases(item, currentAliases));

        return this.readDropdownOption<TItem>(option);
    }

    private optionMatchesAliases(option: FilteredValue<unknown>, aliases: readonly string[]): boolean {
        const optionAliases = [option.value, ...(option.valueAliases ?? [])]
            .flatMap((value) => this.toComparableAliases(value));

        return optionAliases.some((alias) => aliases.includes(alias));
    }

    private rawMatchesAliases(rawValue: Record<string, unknown>, aliases: readonly string[]): boolean {
        const rawAliases = [
            rawValue['catalogKey'],
            rawValue['catalogCode'],
            rawValue['id'],
            rawValue['codigo'],
            rawValue['codigo_cultivo'],
            rawValue['id_cultivo'],
            rawValue['id_variedad'],
            rawValue['codigo_variedad'],
        ].flatMap((value) => this.toComparableAliases(value));

        return rawAliases.some((alias) => aliases.includes(alias));
    }

    private asRecord<TItem extends Record<string, unknown>>(value: unknown): TItem | null {
        return value !== null && typeof value === 'object' ? value as TItem : null;
    }

    private toComparableAliases(value: unknown): string[] {
        const rawValue = this.toStringValue(value);

        if (rawValue === '') {
            return [];
        }

        const parts = rawValue.split(':').filter((part) => part !== '');

        return Array.from(new Set([rawValue, ...parts]));
    }

    private extractCatalogKeyPart(value: unknown, index: number): string {
        const rawValue = this.toStringValue(value);
        const parts = rawValue.split(':');

        return parts.length > index ? this.toStringValue(parts[index]) : '';
    }

    private resolveFirstValue(...values: readonly unknown[]): string {
        for (const value of values) {
            const normalizedValue = this.toStringValue(value);

            if (normalizedValue !== '') {
                return normalizedValue;
            }
        }

        return '';
    }

    private toStringValue(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }

        return String(value).trim();
    }

    private openSigpac() {
        this.sigpacDialog?.openDialog();
    }
}